import {
  type AttemptLocks,
  type AttemptStorage,
  browserAttemptStorage,
  createUnresolvedAttemptStore,
  reconcileAttemptAtSend,
  withAttemptLock,
} from "@/shared/utils/unresolvedAttempt";

import type { CreateWalletDebitPayload } from "../types/walletDebits.types";

/** A debit sent but whose outcome is not known yet: kept until the backend says what became of it. */
export interface WalletDebitAttempt {
  payload: CreateWalletDebitPayload;
  /** For the notice; the wallet list may not be loaded when it shows. */
  currency: string | null;
  /** When the request left (epoch ms); absent on an attempt stored before it was recorded. */
  sent_at?: number;
}

export type WalletDebitLookup = "not_placed" | "pending" | "unknown";

export const WALLET_DEBIT_NOT_FOUND_CODE = "wallet_debit_not_found";
export const WALLET_DEBIT_IN_PROGRESS_CODE = "wallet_debit_in_progress";

/**
 * How long after sending a "not found" is not believed: a request still on its way, before the server takes
 * its key lock, is invisible to the lookup.
 */
export const WALLET_DEBIT_NOT_FOUND_GRACE_MS = 60_000;

/**
 * What a failed lookup by key means. Only these two codes are answers; anything else is not knowing yet. A
 * "not found" within the grace after `sentAt` reads as still pending.
 */
export function settleWalletDebitLookup(
  status: number | null,
  code: string | null,
  sentAt: number | null = null,
  now: number = Date.now(),
): WalletDebitLookup {
  if (status === 404 && code === WALLET_DEBIT_NOT_FOUND_CODE) {
    return sentAt !== null && now - sentAt < WALLET_DEBIT_NOT_FOUND_GRACE_MS ? "pending" : "not_placed";
  }
  if (status === 409 && code === WALLET_DEBIT_IN_PROGRESS_CODE) return "pending";
  return "unknown";
}

type DebitBody = Omit<CreateWalletDebitPayload, "idempotency_key">;

/** The same debit as the stored one: retrying it must reuse the stored key, so the two can never both debit. */
export function isSameDebit(stored: CreateWalletDebitPayload, next: DebitBody): boolean {
  return (
    stored.organization_id === next.organization_id &&
    stored.wallet_id === next.wallet_id &&
    stored.amount.trim() === next.amount.trim() &&
    (stored.rail ?? null) === (next.rail ?? null) &&
    (stored.country ?? null) === (next.country ?? null) &&
    stored.concept.trim() === next.concept.trim() &&
    (stored.external_reference ?? "").trim() === (next.external_reference ?? "").trim() &&
    stored.internal_note.trim() === next.internal_note.trim()
  );
}

/** Right before a debit leaves, against what the store holds now (see `reconcileAttemptAtSend`). */
export function reconcileDebitAtSend(
  stored: WalletDebitAttempt | null,
  outgoing: CreateWalletDebitPayload,
): { payload: CreateWalletDebitPayload; adopted: boolean } | null {
  return reconcileAttemptAtSend(stored, outgoing, isSameDebit);
}

/** Refused before sending: another tab held the organization's debit lock for too long. */
export class WalletDebitBusyError extends Error {
  constructor() {
    super("Another tab is sending a debit to this organization");
    this.name = "WalletDebitBusyError";
  }
}

/** One send of a debit, holding the organization's debit lock shared by every tab of this browser. */
export function withWalletDebitLock<T>(
  organizationId: string,
  run: () => Promise<T>,
  locks?: AttemptLocks | null,
  waitMs?: number,
): Promise<T> {
  return withAttemptLock(
    `coloca.walletDebit:${organizationId}`,
    run,
    () => new WalletDebitBusyError(),
    locks,
    waitMs,
  );
}

const STORAGE_KEY = "coloca.walletDebit.unresolved";

// A missing corridor or reference reads as none: dropping a stored attempt would let a second debit through.
const isOptionalString = (value: unknown) => value == null || typeof value === "string";

const isAttempt = (value: unknown): value is WalletDebitAttempt => {
  const attempt = value as Partial<WalletDebitAttempt> | null;
  const payload = attempt?.payload;
  return (
    !!payload &&
    typeof payload.organization_id === "string" &&
    typeof payload.wallet_id === "string" &&
    typeof payload.amount === "string" &&
    isOptionalString(payload.rail) &&
    isOptionalString(payload.country) &&
    typeof payload.concept === "string" &&
    isOptionalString(payload.external_reference) &&
    typeof payload.internal_note === "string" &&
    typeof payload.idempotency_key === "string"
  );
};

/** One unresolved debit per organization, apart from the top-ups' (its own storage key and lock). */
export function createUnresolvedDebitStore(storage: AttemptStorage | null) {
  return createUnresolvedAttemptStore(storage, STORAGE_KEY, isAttempt);
}

export const unresolvedWalletDebits = createUnresolvedDebitStore(browserAttemptStorage());
